import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Topbar } from '../components/Topbar';
import { 
  FileText, CheckCircle2, Calendar, MessageCircle, 
  Info, User, GraduationCap, Clock, Users, Shield,
  Trash2, AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, Button, Badge } from '../components/ui';
import { useToast } from '../context/ToastContext';

export function MyEnrollmentPage() {
  const { user } = useAuth();
  const { allEnrollments, removeEnrollment } = useData();
  const { addToast } = useToast();
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Buscar a matrícula do aluno logado em allEnrollments
  const enrollment = (allEnrollments || []).find(e => 
    e.enrollmentId === user?.enrollmentId || e.citizenId === user?.citizenId
  );
  
  const profilePicture = enrollment?.profilePicture || null;

  const handleCancelEnrollment = () => {
    if (!enrollment) return;
    
    const confirmed = window.confirm(`Tem certeza que deseja cancelar sua matrícula em ${enrollment.course || enrollment.courseName}? Esta ação é irreversível.`);
    
    if (confirmed) {
      setIsCancelling(true);
      const result = removeEnrollment(enrollment.enrollmentId);
      if (result.success) {
        addToast('Sua matrícula foi cancelada com sucesso.', 'success');
      } else {
        addToast('Erro ao cancelar matrícula.', 'error');
      }
      setIsCancelling(false);
    }
  };

  // Cenário 1 - Aluno não matriculado
  if (!enrollment) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Topbar />
        <div className="flex flex-col items-center justify-center min-h-[70vh] text-center p-6">
          <div className="w-24 h-24 bg-yellow/10 rounded-full flex items-center justify-center mb-8">
            <GraduationCap size={48} className="text-yellow" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Minha Matrícula</h2>
          <p className="text-xl text-white mb-2 font-medium">Você ainda não possui uma matrícula ativa.</p>
          <p className="text-muted max-w-md mx-auto mb-10 leading-relaxed">
            Para acessar as informações acadêmicas, primeiro realize sua matrícula em um dos cursos disponíveis no catálogo da instituição.
          </p>
          <Button 
            className="px-10 py-4 text-lg font-bold h-auto rounded-2xl" 
            onClick={() => window.location.href = '/cursos'}
          >
            Realizar Matrícula
          </Button>
        </div>
      </motion.div>
    );
  }

  // Cenário 2 - Aluno matriculado
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Topbar />
      
      <div className="max-w-6xl mx-auto py-10 px-6">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-purple/15 rounded-3xl flex items-center justify-center text-purple shadow-inner border border-purple/20">
              <FileText size={32} />
            </div>
            <div>
              <h2 className="text-4xl font-black tracking-tight">Minha Matrícula</h2>
              <p className="text-muted font-medium text-lg">Central de referência acadêmica do estudante.</p>
            </div>
          </div>
          <Badge variant="purple" className="px-6 py-2 rounded-2xl text-sm font-black uppercase tracking-widest self-start md:self-auto">
            Matrícula {enrollment.status || 'Ativa'}
          </Badge>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Coluna Principal */}
          <div className="lg:col-span-8 space-y-8">
            {/* Dados da Matrícula */}
            <div className="bg-[#0a1120] border border-[#1d2a42] rounded-[2.5rem] p-8 md:p-10 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple/5 blur-[80px] rounded-full -mr-32 -mt-32" />
              
              <h3 className="text-xl font-black mb-10 flex items-center gap-3">
                <User size={20} className="text-purple" />
                DADOS DO ESTUDANTE
              </h3>

              <div className="flex flex-col md:flex-row gap-10 items-start mb-12">
                <div className="w-32 h-32 rounded-3xl border-4 border-[#1d2a42] bg-[#070d19] flex items-center justify-center shrink-0 shadow-xl">
                  <div className="w-full h-full flex items-center justify-center text-white bg-gradient-to-br from-purple/20 to-purple/5">
                    <User size={48} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 relative z-10 flex-1 w-full">
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] uppercase font-black tracking-[2px] text-muted">Nome Completo</span>
                    <strong className="text-xl text-white">{enrollment.fullName || user.name}</strong>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] uppercase font-black tracking-[2px] text-muted">Número da Matrícula</span>
                    <strong className="text-xl font-mono text-purple">{enrollment.enrollmentId}</strong>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] uppercase font-black tracking-[2px] text-muted">Curso de Graduação</span>
                    <strong className="text-xl text-white">{enrollment.course || enrollment.courseName}</strong>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] uppercase font-black tracking-[2px] text-muted">Turma Designada</span>
                    <strong className="text-xl text-white">Turma {enrollment.turma}</strong>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] uppercase font-black tracking-[2px] text-muted">Status Acadêmico</span>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green animate-pulse" />
                      <strong className="text-lg text-green uppercase font-black tracking-wider">Ativo</strong>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] uppercase font-black tracking-[2px] text-muted">Data de Ingresso</span>
                    <strong className="text-xl text-white">
                      {enrollment.date ? new Date(enrollment.date).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Situação Acadêmica */}
            <div className="bg-[#0a1120] border border-[#1d2a42] rounded-[2.5rem] p-8 md:p-10 shadow-xl">
              <h3 className="text-xl font-black mb-8 flex items-center gap-3">
                <Shield size={20} className="text-purple" />
                SITUAÇÃO ACADÊMICA
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="bg-green/10 border border-green/20 p-6 rounded-[2rem] flex flex-col items-center gap-4 text-center group hover:bg-green/15 transition-all">
                  <div className="w-14 h-14 bg-green/20 rounded-2xl flex items-center justify-center text-green shadow-lg">
                    <CheckCircle2 size={28} />
                  </div>
                  <div>
                    <span className="block font-black text-xs text-green uppercase tracking-widest mb-1">Status</span>
                    <span className="font-bold text-sm text-white">Matrícula Confirmada</span>
                  </div>
                </div>

                <div className="bg-green/10 border border-green/20 p-6 rounded-[2rem] flex flex-col items-center gap-4 text-center group hover:bg-green/15 transition-all">
                  <div className="w-14 h-14 bg-green/20 rounded-2xl flex items-center justify-center text-green shadow-lg">
                    <Users size={28} />
                  </div>
                  <div>
                    <span className="block font-black text-xs text-green uppercase tracking-widest mb-1">Alocação</span>
                    <span className="font-bold text-sm text-white">Turma Definida</span>
                  </div>
                </div>

                <div className="bg-green/10 border border-green/20 p-6 rounded-[2rem] flex flex-col items-center gap-4 text-center group hover:bg-green/15 transition-all">
                  <div className="w-14 h-14 bg-green/20 rounded-2xl flex items-center justify-center text-green shadow-lg">
                    <Shield size={28} />
                  </div>
                  <div>
                    <span className="block font-black text-xs text-green uppercase tracking-widest mb-1">Garantia</span>
                    <span className="font-bold text-sm text-white">Vaga Garantida</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Coluna Lateral */}
          <div className="lg:col-span-4 space-y-8">
            {/* Próximos Passos */}
            <div className="bg-[#0a1120] border border-[#1d2a42] rounded-[2.5rem] p-8 shadow-xl">
              <h3 className="text-lg font-black mb-6 flex items-center gap-3 uppercase tracking-tighter">Próximos Passos</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4 text-green">
                  <div className="w-8 h-8 rounded-xl bg-green/10 flex items-center justify-center shrink-0 border border-green/20">
                    <CheckCircle2 size={16} />
                  </div>
                  <span className="text-sm font-black uppercase tracking-tight">Matrícula realizada</span>
                </div>
                <div className="flex items-center gap-4 text-green">
                  <div className="w-8 h-8 rounded-xl bg-green/10 flex items-center justify-center shrink-0 border border-green/20">
                    <CheckCircle2 size={16} />
                  </div>
                  <span className="text-sm font-black uppercase tracking-tight">Turma definida</span>
                </div>
                <div className="flex items-center gap-4 text-muted group cursor-pointer hover:text-white transition-colors">
                  <div className="w-8 h-8 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center shrink-0 group-hover:border-purple/50 group-hover:bg-purple/10">
                    <div className="w-2 h-2 rounded-full bg-white/20 group-hover:bg-purple" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-tight">Acessar Discord Oficial</span>
                </div>
                <div className="flex flex-col gap-4 text-muted">
                  <div className="w-8 h-8 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-white/20" />
                  </div>
                  <span className="text-sm font-black uppercase tracking-tight">Aguardar Cronograma</span>
                </div>
              </div>
            </div>

            {/* Zona de Perigo - Cancelamento */}
            <Card className="border-red/20 bg-red/5 p-8">
              <h3 className="text-red font-black text-sm uppercase tracking-widest flex items-center gap-2 mb-4">
                <AlertTriangle size={18} /> Zona de Perigo
              </h3>
              <p className="text-xs text-muted font-medium mb-6 leading-relaxed">
                Ao cancelar sua matrícula, você perderá o acesso a todas as disciplinas e notas atuais. Esta ação permitirá que você escolha um novo curso no catálogo.
              </p>
              <Button 
                variant="outline" 
                className="w-full py-4 border-red/30 text-red hover:bg-red/10 font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
                onClick={handleCancelEnrollment}
                disabled={isCancelling}
              >
                <Trash2 size={14} /> {isCancelling ? 'CANCELANDO...' : 'CANCELAR MINHA MATRÍCULA'}
              </Button>
            </Card>

            {/* Aviso Importante Discord */}
            <div className="bg-[#5865F2] rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-[#5865F2]/20 cursor-pointer hover:brightness-110 transition-all">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                    <MessageCircle size={24} className="fill-white" />
                  </div>
                  <span className="font-black text-[10px] uppercase tracking-[4px]">Comunicados</span>
                </div>
                <h4 className="text-2xl font-black mb-4 leading-tight">Canal Exclusivo Discord</h4>
                <p className="text-sm text-white/90 leading-relaxed mb-8 font-bold">
                  Os horários das aulas, eventos e comunicados são divulgados exclusivamente através do Discord oficial da instituição.
                </p>
                <Button 
                  className="w-full bg-white text-[#5865F2] hover:bg-white/90 border-none font-black py-4 rounded-2xl shadow-xl flex items-center justify-center gap-3"
                  onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}
                >
                  INGRESSAR AGORA
                </Button>
              </div>
              {/* Efeito Visual */}
              <MessageCircle size={180} className="absolute -right-12 -bottom-12 text-white/10 rotate-12 group-hover:scale-110 transition-transform duration-700" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
